/**
 * @lodex-pro/web-admin/ui/bits — shared presentational primitives (Task 25.2).
 *
 * Inline-style idiom matching web-dispatch's sites-operators/management bits, using
 * the shared lodeX Pro dark design tokens (var(--color-*)). Kept web-admin-local so
 * the admin console has no dependency on web-dispatch feature code.
 */
import * as React from 'react';
import { Plus } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import { StatusLabel, type StatusTone } from 'src/components/ui/status-label';

export const fieldStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '40px',
  padding: '8px 10px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
};

export const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '4px',
  fontSize: '13px',
  fontWeight: 500,
  color: 'var(--color-text-secondary)',
};

export const searchInputStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '38px',
  padding: '8px 10px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
};

export const subText: React.CSSProperties = {
  fontSize: '12px',
  color: 'var(--color-text-tertiary)',
};

export function FormRow({
  label,
  htmlFor,
  required,
  hint,
  children,
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  hint?: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div>
      <label htmlFor={htmlFor} style={labelStyle}>
        {label}
        {required === true && <span style={{ color: 'var(--color-danger)' }}> *</span>}
      </label>
      {children}
      {hint !== undefined && (
        <p style={{ ...subText, marginTop: '4px', textTransform: 'none' }}>{hint}</p>
      )}
    </div>
  );
}

type PillTone = 'neutral' | 'positive' | 'muted' | 'accent' | 'danger' | 'warning';

/** A status tone shows as a dot plus a sentence-case word (restyle, 2026-10-28). */
const STATUS_TONES: Partial<Record<PillTone, StatusTone>> = {
  positive: 'success',
  danger: 'danger',
  warning: 'warning',
  muted: 'neutral',
};

/**
 * A status ("Paid", "Overdue", "Archived") or a tag ("TX", "Add-on"). A status is a dot plus a
 * sentence-case word; a tag (neutral, accent) is a small rounded chip showing the label as given.
 */
export function Pill({
  label,
  tone = 'neutral',
}: {
  label: string;
  tone?: PillTone;
}): React.JSX.Element {
  const status = STATUS_TONES[tone];
  if (status !== undefined) {
    return (
      <StatusLabel tone={status} style={{ fontSize: '13px', fontWeight: 500 }}>
        {label}
      </StatusLabel>
    );
  }
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 'var(--radius-pill)',
        fontSize: '13px',
        fontWeight: 500,
        backgroundColor: 'var(--color-bg-tertiary)',
        color: tone === 'accent' ? 'var(--color-accent-blue-dark)' : 'var(--color-text-secondary)',
      }}
    >
      {label}
    </span>
  );
}

export function StatusBadge({ active }: { active: boolean }): React.JSX.Element {
  return <Pill label={active ? 'Active' : 'Suspended'} tone={active ? 'positive' : 'danger'} />;
}

export function AddButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <Button
      type="button"
      onClick={onClick}
      style={{
        backgroundColor: 'var(--color-accent-blue)',
        color: 'var(--color-on-accent)',
        borderColor: 'var(--color-accent-blue)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
      }}
    >
      <Plus size={16} aria-hidden="true" /> {label}
    </Button>
  );
}

/** A slug is derived from a name: lowercase, alphanumerics, single dashes. */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** HaulId preview for a given abbreviation (Task 25.2 / 25.7 — `{ABBR}-OPR000000001`). */
export function haulIdPreview(abbrev: string): string {
  const a = abbrev.trim().toUpperCase() || 'ABC';
  return `${a}-OPR000000001`;
}
